import React, { useContext, useEffect, useState } from "react";

import { FormButton, FormCheckbox } from "@/components/Form";
import { PRIVACY_STATEMENT_LINK_AS } from "@/config";
import { GlobalContext } from "@/contexts";
import LocaleContext from "@/i18n";
import IFrameService from "@/service/IFrame";
import {
  QuestionInteractionOutro,
  QuestionOrAdvertisingOutro,
} from "@/service/Model";
import { Option, QuestionConfigData, QuestionSet } from "@/service/Permutive";
import TrackingService from "@/service/Tracking";
import Svg from "@/styles/Svg";

import { OutroPropsExtented } from "../index";
import { OutroFeedback, OutroFeedbackIcon } from "../styles";
import {
  Paging,
  QuestionFieldset,
  QuestionOption,
  QuestionTitle,
  Step,
  StepWrapper,
} from "./styles";
interface QuestionProps extends OutroPropsExtented {
  outro: QuestionInteractionOutro | QuestionOrAdvertisingOutro;
}
const Question: React.FC<QuestionProps> = (
  props: QuestionProps
): React.JSX.Element => {
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [optIn, setOptIn] = useState(false);
  const [completed, setCompleted] = useState(false);
  const { genderRequest, ageRequest, incomeRequest } = props.outro;
  const globalContext = useContext(GlobalContext);
  const localeContext = useContext(LocaleContext);
  const { translation } = localeContext;
  const { icons } = globalContext;
  const questionSets: QuestionSet[] = [];

  useEffect(() => {
    TrackingService.getInstance().trackOnOutroImpression();
  }, []);

  const handleOptionClick = (key: string, value: string | string[]) => {
    IFrameService.sendPermutiveDataToAdLib({ [key]: value });

    if (currentQuestion === questionSets.length - 1) {
      setCompleted(true);
    } else {
      setCurrentQuestion(currentQuestion + 1);
    }
    TrackingService.getInstance().trackOnOutroClick();
  };

  const renderQuestion = (questionSet: QuestionSet): React.JSX.Element => {
    const htmlOptions = questionSet.options.map((option: Option) => (
      <QuestionOption
        key={option.text}
        onClick={() => handleOptionClick(questionSet.key, option.value)}
      >
        {option.text}
      </QuestionOption>
    ));

    return (
      <div data-qa={"question"}>
        <QuestionTitle>{questionSet.question}</QuestionTitle>
        <QuestionFieldset>{htmlOptions}</QuestionFieldset>
      </div>
    );
  };

  const renderThankYou = (): React.JSX.Element => {
    return (
      <OutroFeedback>
        <OutroFeedbackIcon success={true}>
          <Svg contents={icons.Success} height={65} width={65} />
        </OutroFeedbackIcon>
        <h3>{translation.thankYou}</h3>
      </OutroFeedback>
    );
  };

  const renderOptIn = (): React.JSX.Element => (
    <>
      <FormCheckbox
        icons={icons}
        name={"questionOutroOptIn"}
        label={translation.questionOutro.advertisementOptInLabelAS(
          PRIVACY_STATEMENT_LINK_AS
        )}
        onChange={globalContext.setQuestionOutroOptIn}
        checked={globalContext.questionOutroOptIn}
      />
      <FormButton
        disabled={!globalContext.questionOutroOptIn}
        label={translation.next}
        onClick={() => setOptIn(globalContext.questionOutroOptIn)}
      />
    </>
  );

  if (genderRequest) {
    questionSets.push(QuestionConfigData.gender);
  }

  if (ageRequest) {
    questionSets.push(QuestionConfigData.age_range);
  }

  if (incomeRequest) {
    questionSets.push(QuestionConfigData.income_range);
  }

  if (!questionSets.length) {
    return <></>;
  }

  return (
    <>
      <StepWrapper>
        <Step active={!optIn}>{renderOptIn()}</Step>
        {questionSets.map((questionSet: QuestionSet, index: number) => (
          <Step
            key={questionSet.question}
            active={optIn && !completed && index === currentQuestion}
          >
            {renderQuestion(questionSet)}
          </Step>
        ))}
        <Step active={completed}>{renderThankYou()}</Step>
      </StepWrapper>
      {optIn && (
        <Paging>{`${currentQuestion + 1}/${questionSets.length}`}</Paging>
      )}
    </>
  );
};

export default Question;
